import { useEffect, useState } from 'react'
import { get } from 'lodash'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import Select from 'react-select'

import Typography from '../../typography'
import Card from '../card'
import Image from '../../image'

import {
  PROVIDERS,
  PROVIDERS_ICON_MAP,
  PROVIDERS_TEXT_MAP,
  PROVIDERS_COLOR_MAP,
  PROVIDERS_BWICON_MAP,
  PROVIDERS_RASTER_LOGOS,
  PROVIDERS_LOGO_WIDTH_MAP,
  PROVIDERS_LOGO_HEIGHT_MAP
} from '../constants'
import { useBreakpoint } from '../../../../hooks/breakpoint'

import { getCategories, getCourses as getItems } from '../../../../api/bantrab'
import { COURSE_CATEGORIES_LIST_QUERY, COURSE_CATEGORY, COURSES_LIST_QUERY as ITEMS_LIST_QUERY } from '../../../../api/bantrab/queries'

const ITEMS_PER_PAGE = parseInt(process.env.NEXT_PUBLIC_BANTRAB_COURSES_PER_PAGE_ON_LISTING)

const Courses = ({ courses, integrationSettings, parametro, ...attrs }) => {
  const breakpoint = useBreakpoint()
  const [isLoading, setIsLoading] = useState(0)
  const [displayFetchButton, setDisplayFetchButton] = useState(true)
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  const [categories, setCategories] = useState([])
  const [selectedCategory, setSelectedCategory] = useState({ label: 'Todas las categorías', value: -1 })

  const providers = [
    ...new Set(
      Object.keys(
        courses
      ).map(
        provider => {
          const count = get(courses[provider], 'meta.totalCount', 0)
          if (count === 0 || !PROVIDERS.includes(provider.split('-')[0])) {
            return null
          }
          return provider.split('-')[0]
        }
      ).filter(
        provider => provider !== null
      )
    )
  ]

  const [activeProvider, setActiveProvider] = useState(parametro ?? providers[0].split('-')[0])
  const [innerCourses, setInnerCourses] = useState(() => {
    return Object.fromEntries(
      providers.map(
        provider => {
          let items = get(courses, provider, {})
          if (provider === 'lachiripa') {
            items = {
              serie: get(courses, 'lachiripa-serie', {}),
              capsule: get(courses, 'lachiripa-capsule', {})
            }
          }
          return [provider, items]
        }
      )
    )
  })

  useEffect(
    () => {
      if (activeProvider === 'lachiripa') {
        const totalSerieItems = get(innerCourses[activeProvider].serie, 'meta.totalCount', 0)
        const fetchedSerieItems = get(innerCourses[activeProvider].serie, 'items', []).length
        const totalCapsuleItems = get(innerCourses[activeProvider].capsule, 'meta.totalCount', 0)
        const fetchedCapsuleItems = get(innerCourses[activeProvider].capsule, 'items', []).length
        setDisplayFetchButton({
          serie: fetchedSerieItems < totalSerieItems,
          capsule: fetchedCapsuleItems < totalCapsuleItems
        })
      } else {
        const totalItems = get(innerCourses[activeProvider], 'meta.totalCount', 0)
        const fetchedItems = get(innerCourses[activeProvider], 'items', []).length
        setDisplayFetchButton(fetchedItems < totalItems)
      }
    },
    [activeProvider, innerCourses]
  )
  useEffect(
    async () => {
      setIsLoading(false)
    },
    [innerCourses]
  )

  const fetchMoreCourses = async () => {
    try {
      setIsLoading(true)

      if (activeProvider === 'lachiripa') {
        const offset = get(innerCourses[activeProvider][lachiripaType], 'items', []).length

        const query = {
          ...ITEMS_LIST_QUERY,
          offset: offset,
          limit: ITEMS_PER_PAGE,
          provider: activeProvider,
          has_specialization: false,
          lachiripa_type: lachiripaType
        }

        let fetchedCourses = await getItems(query)
        fetchedCourses = await fetchedCourses.json()

        const updatedCourses = { ...innerCourses }
        updatedCourses[activeProvider][lachiripaType].meta = {
          ...updatedCourses[activeProvider][lachiripaType].meta,
          ...fetchedCourses.meta
        }
        updatedCourses[activeProvider][lachiripaType].items = updatedCourses[activeProvider][lachiripaType].items.concat(fetchedCourses.items)

        setInnerCourses(updatedCourses)
      } else {
        const currentCourses = innerCourses[activeProvider]
        const offset = get(currentCourses, 'items', []).length
        const query = {
          ...ITEMS_LIST_QUERY,
          offset: offset,
          limit: ITEMS_PER_PAGE,
          provider: activeProvider,
          has_specialization: false
        }
        if (debouncedSearchTerm) {
          query.search = debouncedSearchTerm
        }

        if (selectedCategory.value !== -1) {
          query.category = selectedCategory.value
        }

        const response = await getItems(query)
        const fetchedCourses = await response.json()

        if (!fetchedCourses?.items?.length) {
          setDisplayFetchButton(false)
          return
        }

        setInnerCourses(prev => ({
          ...prev,
          [activeProvider]: {
            meta: fetchedCourses.meta,
            items: [...(prev[activeProvider]?.items || []), ...fetchedCourses.items]
          }
        }))

      }
    } catch (error) {
      console.error('Error fetching more courses:', error)
    } finally {
      setIsLoading(false)
    }
  }

  attrs.className = classNames(
    attrs.className,
    [
      'bg-white',
      'relative',
      'md:pt-24',
      'md:pb-12',
      'pt-20',
      'pb-10'
    ]
  )
  useEffect(() => {
    const fetchCoursesByCategory = async () => {
      try {
        setIsLoading(true);

        const query = {
          ...ITEMS_LIST_QUERY,
          category: selectedCategory.value !== -1 ? selectedCategory.value : undefined,
          provider: activeProvider,
          limit: ITEMS_PER_PAGE
        };

        if (selectedCategory.value === -1) {
          delete query.category;
        }

        const response = await getItems(query);

        if (!response.ok) {
          console.error('Error fetching courses:', response.statusText);
          return;
        }

        const fetchedCourses = await response.json();

        if (!fetchedCourses || !fetchedCourses.items) {
          console.error('Invalid response format:', fetchedCourses);
          return;
        }

        const updatedCourses = { ...innerCourses };
        updatedCourses[activeProvider] = {
          meta: fetchedCourses.meta,
          items: fetchedCourses.items
        };

        setInnerCourses(updatedCourses);
      } catch (error) {
        console.error('Error in fetchCoursesByCategory:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCoursesByCategory();
  }, [selectedCategory, activeProvider]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  const handleInputChange = (event) => {
    setSearchTerm(event.target.value);
  };

  useEffect(async () => {
    const query = {
      ...COURSE_CATEGORIES_LIST_QUERY,
    };
    const response = await getCategories(query)
    const { items } = await response.json()
    const parsedItems = [{ label: 'Todas las categorías', value: -1 }]
      .concat(
        items.map((element) => {
          return { label: element.description, value: element.id }
        })
      )
    setCategories(parsedItems)
  }, [])


  useEffect(() => {
    const fetchCourses = async () => {
      if (debouncedSearchTerm) {
        const query = {
          ...ITEMS_LIST_QUERY,
          search: debouncedSearchTerm,
          provider: activeProvider,
        };
        let fetchedCourses = await getItems(query);
        fetchedCourses = await fetchedCourses.json();

        const updatedCourses = { ...innerCourses };
        updatedCourses[activeProvider].meta = {
          ...updatedCourses[activeProvider].meta,
          ...fetchedCourses.meta,
        };

        updatedCourses[activeProvider].items = fetchedCourses.items;

        setInnerCourses(updatedCourses);
      } else {
        const query = {
          ...ITEMS_LIST_QUERY,
          provider: activeProvider,
          limit: ITEMS_PER_PAGE,
          category: selectedCategory.value !== -1 ? selectedCategory.value : undefined,
        };

        if (selectedCategory.value === -1) {
          delete query.category;
        }

        let fetchedCourses = await getItems(query);
        fetchedCourses = await fetchedCourses.json();

        const updatedCourses = { ...innerCourses };
        updatedCourses[activeProvider] = {
          meta: fetchedCourses.meta,
          items: fetchedCourses.items
        };

        setInnerCourses(updatedCourses);
      }
    };
    fetchCourses();
  }, [debouncedSearchTerm, activeProvider]);

  const handleActiveProvider = (provider) => {
    setSearchTerm('');
    setActiveProvider(provider);

    // Resetea las categorias cuando se cambia a bancaaprende
    if (provider === 'bancaaprende') {
      setSelectedCategory({ label: 'Todas las categorías', value: -1 });
    }
  }

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      if (!searchTerm.trim()) {
        setSearchTerm('');
        setDebouncedSearchTerm('');
      }
    }
  };

  const handleFetchMoreClick = async () => {
    await fetchMoreCourses()
  }

  return <section {...attrs}>
    <div
      className={
        classNames([
          'relative',
          'container',
          'px-4',
          'mb-10'
        ])
      }>
      <div
        className={
          classNames([
            'flex',
            'flex-row',
            'overflow-auto'
          ])
        }>
        {
          providers.map(
            (provider, index) => {
              const isActive = (activeProvider === provider)
              const Logo = isActive ? PROVIDERS_ICON_MAP[provider] : PROVIDERS_BWICON_MAP[provider]
              return <div
                key={index}
                className={
                  classNames([
                    'flex-none',
                    'sm:flex-1',
                    isLoading ? 'cursor-not allowed' : 'cursor-pointer'
                  ])
                }
                style={{
                  flexBasis: breakpoint.mdAndUp ? '33.333333%' : null
                }}
                onClick={() => isLoading ? null : handleActiveProvider(provider)}>
                <div
                  className={'px-4'}>
                  <Typography
                    size={breakpoint.mdAndUp ? 'two' : 'one'}
                    weight={'bold'}
                    className={
                      classNames([
                        'mb-2',
                        'text-center',
                        isActive ? `text-${PROVIDERS_COLOR_MAP[provider]}` : 'text-gray-400'
                      ])
                    }
                    style={{
                      whiteSpace: 'nowrap'
                    }}>
                    {PROVIDERS_TEXT_MAP[provider]}
                  </Typography>
                  {
                    PROVIDERS_RASTER_LOGOS.includes(provider)
                      ? <div className={'h-8 flex items-center justify-center overflow-visible'}>
                        <Image
                          src={Logo}
                          width={PROVIDERS_LOGO_WIDTH_MAP[provider]}
                          height={PROVIDERS_LOGO_HEIGHT_MAP[provider]}
                          className={
                            classNames(
                              [
                                'block',
                                'mx-auto'
                              ],
                              {
                                'text-gray-400': !isActive
                              }
                            )
                          } />
                      </div>
                      : <Logo
                        height={32}
                        className={
                          classNames(
                            [
                              'block',
                              'mx-auto'
                            ],
                            {
                              'text-gray-400': !isActive
                            }
                          )
                        } />
                  }
                </div>
                <div
                  className={
                    classNames([
                      'mt-4',
                      'border-t',
                      isActive ? 'border-turquoise-500' : 'border-transparent'
                    ])
                  }>
                  <div
                    className={
                      classNames([
                        'border-b',
                        isActive ? 'border-turquoise-500' : 'border-gray-400'
                      ])
                    } />
                </div>
              </div>
            }
          )
        }
      </div>
    </div>
    {
      activeProvider === 'lachiripa'
        ? <>
          {
            innerCourses[activeProvider].serie.items.length > 0
              ? <>
                <div
                  className={
                    classNames([
                      'relative',
                      'container',
                      'px-4',
                      'py-10'
                    ])
                  }>
                  <Typography
                    size={'seven'}
                    weight={'black'}
                    className={'text-center text-red-500'}>
                    La Chiripa F.C.
                  </Typography>
                  <Typography
                    size={'five'}
                    weight={'black'}
                    className={'text-center mb-10 text-turquoise-500'}>
                    La serie
                  </Typography>
                  <div
                    className={
                      classNames([
                        'flex',
                        'flex-row',
                        'flex-wrap',
                        'justify-center',
                        '-m-4'
                      ])
                    }>
                    {
                      innerCourses[activeProvider].serie.items.map(
                        item => {
                          return <Card
                            key={item.id}
                            course={item}
                            integrationSettings={integrationSettings} />
                        }
                      )
                    }
                  </div>
                  <div
                    className="relative">
                    {
                      (displayFetchButton && displayFetchButton.serie)
                        ? <div
                          className={
                            classNames([
                              'w-full',
                              'flex',
                              'flex-row',
                              'justify-center',
                              'text-center',
                              'border',
                              'border-red-500',
                              'text-red-500',
                              'py-3',
                              'mx-auto',
                              'cursor-pointer',
                              'hover:border-red-400',
                              'hover:text-red-400',
                              'active:border-red-600',
                              'active:text-red-600',
                              'mt-8'
                            ])
                          }
                          style={{
                            maxWidth: breakpoint.mdAndUp ? '240px' : '320px',
                            borderRadius: '0 1.5rem 1.5rem 1.5rem'
                          }}
                          onClick={() => fetchMoreCourses({ lachiripaType: 'serie' })}>
                          Ver más cursos
                        </div>
                        : null
                    }
                  </div>
                </div>
              </>
              : null
          }
          {
            innerCourses[activeProvider].capsule.items.length > 0
              ? <>
                <div
                  className={
                    classNames([
                      'relative',
                      'container',
                      'px-4',
                      'py-10'
                    ])
                  }>
                  <div
                    className={
                      classNames([
                        'absolute',
                        'top-0',
                        'bottom-0',
                        'bg-green-50',
                        'z-0'
                      ])
                    }
                    style={{
                      width: '100vw',
                      left: '50%',
                      transform: 'translateX(-50%)'
                    }}
                  />
                  <Typography
                    size={'seven'}
                    weight={'black'}
                    className={'relative text-center text-red-500'}>
                    La Chiripa F.C.
                  </Typography>
                  <Typography
                    size={'five'}
                    weight={'black'}
                    className={'relative text-center mb-10 text-turquoise-500'}>
                    Cápsulas de educación financiera
                  </Typography>
                  <div
                    className={
                      classNames([
                        'relative',
                        'flex',
                        'flex-row',
                        'flex-wrap',
                        'justify-center',
                        '-m-4'
                      ])
                    }>
                    {
                      innerCourses[activeProvider].capsule.items.map(
                        item => {
                          return <Card
                            key={item.id}
                            course={item}
                            integrationSettings={integrationSettings} />
                        }
                      )
                    }
                  </div>
                  <div
                    className="relative">
                    {
                      (displayFetchButton && displayFetchButton.capsule)
                        ? <div
                          className={
                            classNames([
                              'realtive',
                              'w-full',
                              'flex',
                              'flex-row',
                              'justify-center',
                              'text-center',
                              'border',
                              'border-red-500',
                              'text-red-500',
                              'py-3',
                              'mx-auto',
                              'cursor-pointer',
                              'hover:border-red-400',
                              'hover:text-red-400',
                              'active:border-red-600',
                              'active:text-red-600',
                              'mt-8'
                            ])
                          }
                          style={{
                            maxWidth: breakpoint.mdAndUp ? '240px' : '320px',
                            borderRadius: '0 1.5rem 1.5rem 1.5rem'
                          }}
                          onClick={() => fetchMoreCourses({ lachiripaType: 'capsule' })}>
                          Ver más cursos
                        </div>
                        : null
                    }

                  </div>
                </div>
              </>
              : null
          }
        </>
        : <>
          <div
            className={
              classNames([
                'relative',
                'container',
                'px-4',
                'mb-10'
              ])
            }>
            <div
              className={classNames(
                'flex items-center mt-4 mb-10',
                'flex-col md:flex-row'
              )}
            >
              <div className="flex w-full md:w-full mb-4 md:mb-0 md:pr-12">
                <input
                  type="text"
                  placeholder="Buscar..."
                  value={searchTerm}
                  onChange={handleInputChange}
                  onKeyPress={handleKeyPress}
                  className={classNames(
                    'w-full md:w-full',
                    'p-3',
                    'rounded-l-lg',
                    'border',
                    'border-gray-300'
                  )}
                />
                <button
                  className={classNames(
                    'p-3',
                    'rounded-r-lg',
                    'text-white',
                    'hover:bg-blue-600',
                  )}
                  style={{ backgroundColor: 'rgba(20, 117, 142, 1)' }}
                >
                  Buscar
                </button>
              </div>
              {activeProvider !== 'bancaaprende' && (
                <div className={'items-center w-full md:w-1/4'}>
                  <Select
                    value={selectedCategory}
                    menuPlacement={'bottom'}
                    className={'w-full rounded-lg'}
                    onChange={(option) => {
                      setSearchTerm('');
                      setSelectedCategory(option)
                    }}
                    styles={{
                      option: (provided) => ({
                        ...provided,
                        padding: 12,
                        zIndex: 100
                      }),
                      menu: (provided) => ({
                        ...provided,
                        zIndex: 1000
                      }),
                      singleValue: (val) => ({
                        ...val,
                        color: '#8A8C8D'
                      }),
                      control: (val) => ({
                        ...val,
                        height: '3rem',
                        backgroundColor: 'white',
                        color: 'black',
                        borderColor: '#8A8C8D',
                        fontSize: '1rem',
                        fontWeight: 400,
                        zIndex: 100
                      }),
                      indicatorSeparator: () => ({
                        display: 'none'
                      }),
                      dropdownIndicator: (val) => ({
                        ...val,
                        color: '#14758E'
                      })
                    }}
                    options={categories}
                    placeholder="Categorias"
                  />
                </div>
              )}
            </div>
            <div
              className={
                classNames([
                  'flex',
                  'flex-row',
                  'flex-wrap',
                  'justify-center',
                  '-m-4'
                ])
              }>
              {
                innerCourses[activeProvider].items.map(
                  item => {
                    return <Card
                      key={item.id}
                      course={item}
                      integrationSettings={integrationSettings} />
                  }
                )
              }
            </div>
          </div>
          <div
            className={
              classNames([
                'relative',
                'container',
                'px-4'
              ])
            }>
            {
              displayFetchButton
                ? <div
                  className={
                    classNames([
                      'w-full',
                      'flex',
                      'flex-row',
                      'justify-center',
                      'text-center',
                      'border',
                      'border-red-500',
                      'text-red-500',
                      'py-3',
                      'mx-auto',
                      'cursor-pointer',
                      isLoading ? 'opacity-50' : '',
                      'hover:border-red-400',
                      'hover:text-red-400',
                      'active:border-red-600',
                      'active:text-red-600'
                    ])
                  }
                  style={{
                    maxWidth: breakpoint.mdAndUp ? '240px' : '320px',
                    borderRadius: '0 1.5rem 1.5rem 1.5rem'
                  }}
                  onClick={handleFetchMoreClick}
                  disabled={isLoading}
                >
                  {isLoading ? 'Cargando...' : 'Ver más cursos'}
                </div>
                : null
            }
          </div>
        </>
    }
  </section >
}

Courses.propTypes = {
  title: PropTypes.string,
  highlightedTitleText: PropTypes.string,
  courses: PropTypes.object,
  integrationSettings: PropTypes.object
}

export default Courses
